// ============================================================
// SoftwareService.jsx
//
// Landing section explaining platform operations.
//
// Updates:
// - Removed MUI <Container> and <PageWrapper> to allow 100%
//   widescreen, edge-to-edge expansion.
// - Simplified structural CSS directly into the root Box.
// ============================================================

import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { Fade } from "react-awesome-reveal";
import { Button, Box, Typography, Divider, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";

// ── MUI Icons (Brand Consistency) ────────────────────────────
import BrushIcon from "@mui/icons-material/Brush";
import CodeIcon from "@mui/icons-material/Code";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon from "@mui/icons-material/School";
import BuildIcon from "@mui/icons-material/Build";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

// ── Internal Components ──────────────────────────────────────
import RegistrationProcess from "./RegistrationProcess";
import LoadingScreen from "../auth/LoadScreen";

// ============================================================
//  CONSTANTS
// ============================================================

const BRAND = {
    dark:   "#100720",
    gold:   "#E5C95F",
    yellow: "#FFF19B",
    white:  "#FFFFFF",
};

const PROCESS_STEPS = [
    {
        icon: <BrushIcon sx={{ fontSize: 32, color: BRAND.gold }} />,
        title: "Design",
        desc: "Learn to think like a software architect. Plan, diagram, and design scalable systems before writing a single line of code.",
    },
    {
        icon: <CodeIcon sx={{ fontSize: 32, color: BRAND.gold }} />,
        title: "Develop",
        desc: "Build real projects across 16 course tracks using industry-standard tools, an inbuilt compiler, and guided assignments.",
    },
    {
        icon: <RocketLaunchIcon sx={{ fontSize: 32, color: BRAND.gold }} />,
        title: "Deploy",
        desc: "Go beyond tutorials. Premium members get hands-on production deployment experience with live project guidance.",
    },
];

const PLATFORM_HIGHLIGHTS = [
    { icon: <CodeIcon fontSize="small" />,        label: "Inbuilt Online Compiler" },
    { icon: <SchoolIcon fontSize="small" />,      label: "16 Course Tracks" },
    { icon: <BuildIcon fontSize="small" />,       label: "Assignments & Solutions" },
    { icon: <CheckCircleIcon fontSize="small" />, label: "DSA Training" },
    { icon: <EmojiEventsIcon fontSize="small" />, label: "Certification Awards" },
];

// ============================================================
//  STYLED COMPONENTS
// ============================================================

/**
 * FullScreenLoader
 * Covers the entire viewport during page transitions.
 */
const FullScreenLoader = styled(Box)({
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
});

/**
 * TaglineBadge
 * Dark pill badge showing "Design | Develop | Deploy".
 */
const TaglineBadge = styled(Box)({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    backgroundColor: BRAND.dark,
    color: BRAND.white,
    padding: "12px 24px",
    borderRadius: "100px",
    fontSize: "0.95rem",
    fontWeight: 500,
    boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
    flexWrap: "wrap",
});

const BadgeDivider = styled(Box)({
    width: "1px",
    height: "20px",
    backgroundColor: "rgba(255,255,255,0.2)",
});

/**
 * FeatureCard
 * Uses flex: 1 so it naturally grows to fill widescreen space
 * evenly alongside its sibling cards.
 */
const FeatureCard = styled(Box)({
    backgroundColor: BRAND.dark,
    borderRadius: "16px",
    padding: "32px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flex: 1,
    minWidth: "250px", // Ensures they don't crush on small screens
    boxShadow: "0 4px 24px rgba(16,7,32,0.12)",
    transition: "transform 0.2s ease",
    "&:hover": {
        transform: "translateY(-4px)",
    },
});

/**
 * ActionButton
 * Gold CTA button for the Apply Now action.
 */
const ActionButton = styled(Button)({
    marginTop: "48px",
    fontSize: "1rem",
    fontWeight: 700,
    padding: "14px 52px",
    backgroundColor: BRAND.gold,
    color: BRAND.dark,
    borderRadius: "10px",
    textTransform: "none",
    display: "flex",
    gap: "8px",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
        backgroundColor: BRAND.dark,
        color: BRAND.gold,
        transform: "translateY(-2px)",
        boxShadow: "0px 8px 25px rgba(16,7,32,0.25)",
    },
});

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function SoftwareService() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    // ── Effects ───────────────────────────────────────────────
    // Prevent background scrolling while the loading screen is up.
    useEffect(() => {
        document.body.style.overflow = loading ? "hidden" : "unset";
        return () => { document.body.style.overflow = "unset"; };
    }, [loading]);

    // ── Handlers ──────────────────────────────────────────────
    const handleNavigation = (path) => {
        setLoading(true);
        setTimeout(() => navigate(path), 2000);
    };

    return (
        <>
            {/* ── Loading Overlay ── */}
            {loading && (
                <FullScreenLoader>
                    <LoadingScreen message="Preparing your application..." />
                </FullScreenLoader>
            )}

            {/* ── 100% Widescreen Root Container ── */}
            {/* Replaces PageWrapper & Container to ensure true full width */}
            <Box
                component="section"
                sx={{
                    width: "100%",
                    minHeight: "100vh",
                    backgroundColor:"inherit",
                    fontFamily: "'Poppins', sans-serif",
                    boxSizing: "border-box",
                    // Use percentage padding on md+ to allow massive expansion on ultrawide monitors
                    padding: { xs: "80px 20px", md: "100px 5%" },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                {/* Wrap content in a 100% width Box to manage text alignment */}
                <Box sx={{ width: "100%", textAlign: "center" }}>

                    {/* ── Section Label & Headline ── */}
                    <Fade direction="down" triggerOnce>
                        <Typography
                            variant="overline"
                            sx={{
                                fontSize: { xs: "0.75rem", md: "0.85rem" },
                                fontWeight: 800,
                                color: BRAND.dark,
                                letterSpacing: "3px",
                                mb: 2,
                                display: "block",
                            }}
                        >
                            WHAT WE DO & HOW WE OPERATE
                        </Typography>

                        <Typography
                            variant="h2"
                            sx={{
                                fontSize: { xs: "2rem", md: "3rem", lg: "4rem" }, // Scales up for widescreen
                                fontWeight: 900,
                                color: BRAND.dark,
                                fontFamily: "'Georgia', serif",
                                lineHeight: 1.2,
                                mb: 4,
                            }}
                        >
                            Learn. Build.{" "}
                            <Box component="span" sx={{ color: "#b8860b" }}>
                                Get Hired.
                            </Box>
                        </Typography>

                        {/* ── Tagline Badge ── */}
                        <TaglineBadge>
                            <Box display="flex" alignItems="center" gap={1}>
                                <BrushIcon sx={{ color: BRAND.gold, fontSize: 20 }} />
                                <Typography variant="body2" fontWeight={600}>Design</Typography>
                            </Box>
                            <BadgeDivider />
                            <Box display="flex" alignItems="center" gap={1}>
                                <CodeIcon sx={{ color: BRAND.gold, fontSize: 20 }} />
                                <Typography variant="body2" fontWeight={600}>Develop</Typography>
                            </Box>
                            <BadgeDivider />
                            <Box display="flex" alignItems="center" gap={1}>
                                <RocketLaunchIcon sx={{ color: BRAND.gold, fontSize: 20 }} />
                                <Typography variant="body2" fontWeight={600}>Deploy</Typography>
                            </Box>
                        </TaglineBadge>
                    </Fade>

                    {/* ── Feature Cards (Full Width Flex) ── */}
                    <Fade direction="up" delay={200} triggerOnce>
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="stretch"
                            flexWrap="wrap"
                            gap={4} // Increased gap for widescreen
                            mt={8}
                            mb={2}
                            sx={{ width: "100%" }}
                        >
                            {PROCESS_STEPS.map((step) => (
                                <FeatureCard key={step.title}>
                                    {step.icon}
                                    <Typography
                                        variant="h6" fontWeight={800}
                                        sx={{ color: BRAND.gold, fontFamily: "'Georgia', serif" }}
                                    >
                                        {step.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.7, textAlign: "left" }}>
                                        {step.desc}
                                    </Typography>
                                </FeatureCard>
                            ))}
                        </Box>
                    </Fade>

                    {/* ── Visual Divider ── */}
                    <Divider sx={{ my: 8, borderColor: "rgba(16,7,32,0.12)", width: "100%" }} />

                    {/* ── Hands-On Training Section ── */}
                    <Fade direction="up" delay={300} triggerOnce>
                        <Box sx={{ mb: 2, width: "100%" }}>
                            <Typography
                                variant="overline"
                                sx={{
                                    fontSize: "0.8rem", fontWeight: 800,
                                    color: BRAND.dark, letterSpacing: "3px",
                                    display: "block", mb: 1,
                                }}
                            >
                                OUR TRAINING APPROACH
                            </Typography>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontSize: { xs: "1.5rem", md: "2.5rem" },
                                    fontWeight: 800, color: BRAND.dark,
                                    fontFamily: "'Georgia', serif", mb: 6,
                                }}
                            >
                                Hands-On Training
                            </Typography>

                            {/* Assumes RegistrationProcess can also expand width safely */}
                            <RegistrationProcess />
                        </Box>
                    </Fade>

                    {/* ── Platform Feature Chips ── */}
                    <Fade direction="up" delay={350} triggerOnce>
                        <Box
                            display="flex" flexWrap="wrap"
                            justifyContent="center" gap={1.5} mt={6}
                        >
                            {PLATFORM_HIGHLIGHTS.map((item) => (
                                <Chip
                                    key={item.label}
                                    icon={item.icon}
                                    label={item.label}
                                    sx={{
                                        backgroundColor: BRAND.dark,
                                        color: BRAND.gold,
                                        fontWeight: 600,
                                        fontSize: "0.85rem",
                                        padding: "4px 8px",
                                        "& .MuiChip-icon": { color: BRAND.gold },
                                    }}
                                />
                            ))}
                        </Box>
                    </Fade>

                    {/* ── Apply Now Call To Action (CTA) ── */}
                    <Fade direction="up" delay={400} triggerOnce>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <ActionButton onClick={() => handleNavigation("/plan-application-page")}>
                                <EditNoteIcon />
                                Apply Now
                            </ActionButton>
                            <Typography
                                variant="caption"
                                display="block"
                                mt={1.5}
                                sx={{ color: "rgba(16,7,32,0.6)", fontSize: "0.85rem", fontWeight: 500 }}
                            >
                                No commitment required — complete your application in minutes.
                            </Typography>
                        </Box>
                    </Fade>

                </Box>
            </Box>
        </>
    );
}
